import React, {Component} from 'react';
import {get} from '../../util/facebook.settings';
import {ButtonIcon, ButtonPrimary} from '@homeaway/react-buttons';
import {SVGIcon} from '@homeaway/react-svg';
import {FACEBOOK__24} from '@homeaway/svg-defs/lib/defs/FACEBOOK__24';
import {FACEBOOK_COLOR__24} from '@homeaway/svg-defs/lib/defs/FACEBOOK_COLOR__24';
import Script from 'react-load-script';
import edapWrapper from '@homeaway/edap-wrapper';
import {apiPost} from '../../util/api.client';
import PropTypes from 'prop-types';
import ClientLogger from '../../util/clientLogger';
import authAccountTypes from '../../enums/AuthAccountTypes';
import umsSettings from '../../util/ums.settings';
import './FacebookAuthComponent.less';

class FacebookAuthComponent extends Component {
    static get propTypes() {
        return {
            actionLocation: PropTypes.string,
            enableLoading: PropTypes.func,
            disableLoading: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            enableLoading: () => {},
            disableLoading: () => {}
        };
    }

    constructor(props) {
        super(props);
        // Window doesn't exist server side so this is needed, we need to pass a valid host name or component will not load in catalyst app!
        let hostName = (typeof window !== 'undefined') ? window.location.hostname : 'localhost.com';
        let hostSettings = get(hostName, this.props.locale);
        this.state = {
            settings: hostSettings,
            scriptUrl: `https://connect.facebook.net/${hostSettings.locale}/sdk.js`,
            // these are all the fields facebook gives you for free
            // being explicit as light box never makes this call
            scope: 'public_profile,email',
            loggedIn: false,
            locale: this.props.locale
        };
        // per https://developers.facebook.com/docs/javascript/quickstart
        let appId = this.state.settings.appId;
        if (typeof window !== 'undefined') { // Window doesn't exist server side so this is needed or app will fail
            window.fbAsyncInit = function() {
                try {
                    window.FB.init({
                        appId,
                        autoLogAppEvents: true,
                        xfbml: false,
                        version: 'v2.10',
                        oauth: true,
                        status: true
                    });
                } catch (error) {
                    ClientLogger.logError(`An error has occurred while trying to initialize FB script: ${error}`);
                }
            };
        }
        this.onClickFB = this.onClickFB.bind(this);
        this.authFacebook = this.authFacebook.bind(this);
        this.authenticate = this.authenticate.bind(this);
        this.register = this.register.bind(this);
        this.loginAttempt = this.loginAttempt.bind(this);
        this.loginFailure = this.loginFailure.bind(this);
        this.loginSuccess = this.loginSuccess.bind(this);
        this.checkStatus = this.checkStatus.bind(this);
        this.failLogin = this.failLogin.bind(this);
        this.facebookAuthStatus = this.facebookAuthStatus.bind(this);
        this.displayFacebookLabel = this.displayFacebookLabel.bind(this);
        this.handleScriptError = this.handleScriptError.bind(this);
        this.handleScriptLoad = this.handleScriptLoad.bind(this);
        this.handleFacebookError = this.handleFacebookError.bind(this);
        if (this.props.token) {
            this.register(this.props.token, this.props.emailAddress);
        }
    }

    loginSuccess(json) {
        this.setState({loggedIn: true});
        edapWrapper().push({
            name: 'login.success',
            data: {logintype: 'facebook', loginsource: 'lex', publicuuid: json.publicUuid, actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
        this.props.setAuthJson(json);
        this.props.onClose();
    }

    loginFailure() {
        this.setState({loggedIn: false});
        edapWrapper().push({
            name: 'login.failure',
            data: {logintype: 'facebook', loginsource: 'lex', actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
        this.props.handleError(this.props.lingua.read('error.generic'));
    }

    loginAttempt() {
        edapWrapper().push({
            name: 'login.attempt',
            data: {logintype: 'facebook', loginsource: 'lex', actionlocation: this.props.actionLocation},
            options: {'delayProcessing': true}
        });
    }

    handleScriptError() {
        edapWrapper().push({
            name: 'generic.event',
            data: {
                eventaction: 'lexFacebookAuth.facebookScriptError',
                eventcategory: 'lexFacebookAuth',
                actionlocation: this.props.actionLocation
            }
        });
    }

    handleFacebookError() {
        edapWrapper().push({
            name: 'generic.event',
            data: {
                eventaction: 'lexFacebookAuth.facebookScriptResponseError',
                eventcategory: 'lexFacebookAuth',
                actionlocation: this.props.actionLocation
            }
        });
    }

    handleScriptLoad() {
        edapWrapper().push({
            name: 'generic.event',
            data: {
                eventaction: 'lexFacebookAuth.facebookScriptLoad',
                eventcategory: 'lexFacebookAuth',
                actionlocation: this.props.actionLocation
            }
        });
    }

    handleJson(json, email, token) {
        if (json) {
            if (authAccountTypes.isNotUndefined(json.authType)) {
                let SIGNUP = this.checkAuthType(json.authType, authAccountTypes.SIGNUP);
                if (SIGNUP && umsSettings.isTCRequired(this.props.site)) {
                    // this is the SignUp scenario, so we need to update the pageName to TermsAndConditions only for the required EU brands
                    let pageName = 'TermsAndConditions';
                    this.props.performBulkPageUpdate(pageName, email, authAccountTypes.FACEBOOK, token);
                    this.props.disableLoading();
                } else {
                    let umsOnly = this.checkAuthType(json.authType, authAccountTypes.UMS);
                    if (SIGNUP || umsOnly) {
                        this.register(token, email);
                    } else {
                        this.authenticate(token, email);
                    }
                }
            }
        }
    }

    checkAuthType(accountType, loginType) {
        return accountType.some((t) => (t === loginType));
    }

    facebookAuthStatus(token, email) {
        const payload = {
            accessToken: token,
            authType: 'FACEBOOK'
        };

        return new Promise((resolve, reject) => {
            apiPost('/auth/aam/v3/token/status', payload).then((response) => {
                if (response.status >= 200 && response.status < 400) {
                    response.json().then((json) => {
                        this.handleJson(json, email, token);
                        return resolve();
                    });
                }
            }).catch((e) => {
                // If user denied or something else failed we log it, except if they just close pop up, no error should be displayed then
                if (typeof e === 'undefined' || e.error !== 'popup_closed_by_user') {
                    this.loginFailure();
                }
                return reject(e);
            });
        });
    }

    authenticate(token, email) {
        const payload = {
            'userName': email,
            'authType': 'FACEBOOK',
            'secret': token,
            'rememberMe': true
        };

        return new Promise((resolve, reject) => {
            apiPost('/auth/aam/v3/authenticate', payload).then((response) => {
                this.checkStatus(response, resolve, reject);
            }).catch((e) => {
                this.failLogin(e, reject);
            });
        });
    }

    register(token, email) {
        const payload = {
            userName: email,
            secret: token,
            authType: 'FACEBOOK',
            rememberMe: true
        };

        return new Promise((resolve, reject) => {
            apiPost('/auth/aam/v3/register', payload).then((response) => {
                this.checkStatus(response, resolve, reject);
            }).catch((e) => {
                return this.failLogin(e, reject);
            });
        });
    }

    failLogin(e, reject) {
        this.props.disableLoading();
        this.loginFailure();
        return reject(e);
    }

    checkStatus(response, resolve, reject) {
        if (response.status >= 200 && response.status < 400) {
            response.json().then((json) => {
                this.loginSuccess(json);
            });
            return resolve();
        }
        this.props.disableLoading();
        this.loginFailure();
        return reject();
    }

    authFacebook(response) {
        if (response.authResponse) {
            let component = this;
            /* istanbul ignore next */
            window.FB.api('/me', {fields: 'email'}, (me) => {
                let token = response.authResponse.accessToken;
                let email = me.email;
                component.props.enableLoading();
                component.facebookAuthStatus(token, email);
            });
        } else {
            this.handleFacebookError();
        }
    }

    displayFacebookLabel() {
        if (this.props.expediaIconDisplayed === true) {
            return (
                <div className="Facebook_label">
                    <label>{this.props.lingua.read('lex.facebook.icon.label')}</label>
                </div>
            );
        }
        return (
            <div/>
        );
    }

    displayAppropriateButton() {
        if (typeof this.props.authStatus !== 'undefined') {
            return (
                <div className="SocialButtons__icon">
                    <ButtonPrimary label="" block className="btn--facebook btn-block" onClick={this.onClickFB}>
                        <SVGIcon className="btn--spacing" inlineFlex markup={FACEBOOK__24} />
                        <span>{this.props.lingua.read('lex.social.login.with.card.message.facebook')}</span>
                    </ButtonPrimary>
                </div>
            );
        }
        return (
            <div className="small">
                <ButtonIcon circle svgMarkup={FACEBOOK_COLOR__24} label="Facebook" onClick={this.onClickFB}/>
                {this.displayFacebookLabel()}
            </div>
        );
    }

    onClickFB() {
        this.loginAttempt();
        try {
            window.FB.login(this.authFacebook, {scope: this.state.scope, auth_type: 'rerequest'});
        } catch (error) {
            ClientLogger.logError(`An error has occurred while trying to login via FB: ${error}`);
        }
    }

    render() {
        return (
            <div>
                <Script
                    url={this.state.scriptUrl}
                    onError={this.handleScriptError}
                    onLoad={this.handleScriptLoad}
                />
                {this.displayAppropriateButton()}
            </div>
        );
    }

}

export default FacebookAuthComponent;
